import { FC } from 'react'
import { useAccess } from '../../hooks/useAccess'
import { icons } from '../../icons/IconSet'
import { IOrder, IOrderItem } from '../../model/IOrder'
import arrayToNodeString from '../../utils/arrayToNodeString'
import parseISOToString from '../../utils/parseISOToString'
import Divider from '../common/Divider'
import InfoWidget from '../common/widgets/InfoWidget'
import PriceWidget from '../order/PriceWidget'
import { IVendor } from '../../model/IVendor'
import priceFormatter from '../../utils/priceFormatter'

export interface ITicketOrderDetails {
  orderItemId?: IOrderItem['id']
  id?: IOrder['id']
  orderId?: IOrder['orderId']
  siTrackingNum?: IOrder['siTrackingNum']
  siCarrier?: IOrder['siCarrier']
  invoice?: IOrder['invoice']
  fromSite?: IOrder['fromSite']
  siName?: IOrder['siName']
  timeCreate?: IOrder['timeCreate']
  siEmail?: IOrder['siEmail']
  piEmail?: IOrder['piEmail']
  siPhone?: IOrder['siPhone']
  siZip?: IOrder['siZip']
  siState?: IOrder['siState']
  siCity?: IOrder['siCity']
  siStreet?: IOrder['siStreet']
  piName?: IOrder['piName']
  piZip?: IOrder['piZip']
  piState?: IOrder['piState']
  piCity?: IOrder['piCity']
  piStreet?: IOrder['piStreet']
  piPhone?: IOrder['piPhone']
  subtotalPrice?: IOrder['subtotalPrice']
  shipping?: IOrder['shipping']
  taxes?: IOrder['taxes']
  totalPrice?: IOrder['totalPrice']
  refunds?: IOrder['refunds']
  chargeback?: IOrder['chargeback']
  vendorRefunds?: IOrder['vendorRefunds']
  orderItems?: {
    id?: IOrderItem['id']
    pid?: IOrderItem['pid']
    name?: IOrderItem['name']
    vendor?: IOrderItem['vendor']
  }[]
}

const TicketOrderDetails: FC<ITicketOrderDetails> = (ticketOrderDetails) => {
  const { getSettingAccess } = useAccess()
  const {
    orderItemId,
    id,
    orderId,
    timeCreate,
    siName,
    siEmail,
    piEmail,
    siPhone,
    siZip,
    siState,
    siCity,
    siStreet,
    orderItems,
    fromSite,
    siCarrier,
    siTrackingNum,
    invoice,
    piPhone,
    piName,
    piZip,
    piCity,
    piState,
    piStreet,
    refunds,
    chargeback,
    vendorRefunds
  } = ticketOrderDetails

  function getUniqueVendors(): Array<IVendor | null | undefined> {
    const vendorsByIds = (orderItems?.map(({ vendor }) => vendor) || []).reduce(
      (acc, vendor) => ({
        ...acc,
        ...(vendor?.id ? { [vendor.id]: vendor } : {}),
      }),
      {} as Record<string, IVendor>,
    )
    return Object.values(vendorsByIds)
  }

  return !orderId ? null : (
    <div className='flex flex-col gap-4'>
      <div className='flex gap-2 text-md items-center mt-4 font-bold'>
        {icons('user')}
        {piName ?? siName ?? '-'}
      </div>
      <InfoWidget title='Email' key='email-widget'>
        {piEmail ?? siEmail ?? '-'}
      </InfoWidget>
      <InfoWidget title='Primary Phone' key='phone-widget'>
        {piPhone ?? siPhone ?? '-'}
      </InfoWidget>
      <InfoWidget title='Billing Address' key='billing-widget'>
        {arrayToNodeString(', ', [piStreet, piCity, piState, piZip])}
      </InfoWidget>
      <InfoWidget title='Shipping Address' key='shipping-widget'>
        {arrayToNodeString(', ', [siStreet, siCity, siState, siZip])}
      </InfoWidget>
      <Divider />
      <InfoWidget
        key='product-name-widget'
        title={`Product Name${orderItems && orderItems?.length > 1 && !orderItemId ? 's' : ''}`}
      >
        {(orderItemId && orderItems
          ? [orderItems.find(({ id }) => id === orderItemId) || {}]
          : orderItems || []
        ).map(({ pid, name }, i) => (
          <div key={`${name}-${i}`}>
            #{pid}, {name}
          </div>
        ))}
      </InfoWidget>
      <InfoWidget title='Order #' key='order-widget'>
        {orderId ?? '-'}
      </InfoWidget>
      <InfoWidget title='Purchased' key='purchased-widget'>
        {timeCreate ? parseISOToString(timeCreate, false) : '-'}
      </InfoWidget>
      <InfoWidget title='Website' key='website-widget'>
        {fromSite ?? '-'}
      </InfoWidget>
      <InfoWidget title='Vendor' key='vendor-widget'>
        {getUniqueVendors().map((e) => (
          <div key={`${e?.externalKey}`}>
            {e?.name}({e?.externalKey})
          </div>
        ))}
      </InfoWidget>

      <PriceWidget title='Price' {...ticketOrderDetails} />
      <InfoWidget title='Refund' key='refund-widget'>
        <div>
          <div className="flex">
            <div className="w-[80px]">Refund:</div>
            <div className="">{priceFormatter(refunds)}</div>
          </div>
          <div className="flex">
            <div className="w-[80px]">Chargeback:</div>
            <div className="">{priceFormatter(chargeback)}</div>
          </div>
        </div>
      </InfoWidget>
      <InfoWidget title='Vendor Refund' key='vendor-refund-widget'>
        <div>
          <div className="flex">
            <div className="w-[80px]">Refund:</div>
            <div className="">{priceFormatter(vendorRefunds)}</div>
          </div>
        </div>
      </InfoWidget>
      {!!getSettingAccess('order-details') && (
        <div className=''>
          <a
            className='text-primary hover:text-primary_dark'
            href={`${process.env.REACT_APP_BACKEND_URL}/lkq/order-update/${orderId}/`}
            target='_blank'
          >
            View Order Details
          </a>
        </div>
      )}
    </div>
  )
}

export default TicketOrderDetails
